import * as React from 'react';
// @tidal-export alias=./TidalStoriesProvider

import { validateTidalLicense } from '@workwave-tidal/license-utils'
import { type ReactElement, createContext, useContext, useEffect } from 'react'

export type NavigationPosition = 'row' | 'row-reverse'

export type GlobalControlConfig = {
  hideTabs?: boolean
  disableResize?: boolean
  initialSize?: number
  minSize?: number
  hide?: boolean
}

export type GlobalControlsConfig = {
  left?: GlobalControlConfig
  top?: GlobalControlConfig
  right?: GlobalControlConfig
  bottom?: GlobalControlConfig
}

export type TidalStories = {
  disableKeybindings: boolean
  navigationDisabled: boolean
  fullScreen: boolean
  globalControlsConfig: GlobalControlsConfig
  globalControlsDisabled: boolean
  navigationCollapsed: boolean
  navigationWidth: number
  sidebarPosition: NavigationPosition
}

export type LazyResolver<Value> = (prev: Value) => Value

export type TidalStoriesApi = {
  setActiveStory: (uid: string) => void
  setDisableKeybindings: (disable: boolean | LazyResolver<boolean>) => void
  setNavigationDisabled: (disabled: boolean | LazyResolver<boolean>) => void
  setFullScreen: (fullscreen: boolean | LazyResolver<boolean>) => void
  updateGlobalControlsConfig: (partialConfig: GlobalControlsConfig | LazyResolver<GlobalControlsConfig>) => void
  setGlobalControlsConfig: (config: GlobalControlsConfig | LazyResolver<GlobalControlsConfig>) => void
  setGlobalControlsDisabled: (disabled: boolean | LazyResolver<boolean>) => void
  setNavigationCollapsed: (collapsed: boolean | LazyResolver<boolean>) => void
  setNavigationWidth: (width: number | LazyResolver<number>) => void
  setSidebarPosition: (position: NavigationPosition | LazyResolver<NavigationPosition>) => void
}

// ------------------------------------------------------------------------
// Tidal Stories State
// ------------------------------------------------------------------------

const tidalStoriesContext = createContext<TidalStories>({
  navigationDisabled: false,
  disableKeybindings: false,
  fullScreen: false,
  globalControlsConfig: {},
  globalControlsDisabled: false,
  navigationCollapsed: false,
  navigationWidth: 0,
  sidebarPosition: 'row-reverse',
})

// ------------------------------------------------------------------------
// Tidal Stories State API
// ------------------------------------------------------------------------

const tidalStoriesApiContext = createContext<TidalStoriesApi>({
  setActiveStory: () => {},
  setDisableKeybindings: () => {},
  setNavigationDisabled: () => {},
  setFullScreen: () => {},
  updateGlobalControlsConfig: () => {},
  setGlobalControlsConfig: () => {},
  setGlobalControlsDisabled: () => {},
  setNavigationCollapsed: () => {},
  setNavigationWidth: () => {},
  setSidebarPosition: () => {},
})

// ------------------------------------------------------------------------
//  Context Provider
// ------------------------------------------------------------------------

type Props = {
  children: ReactElement | ReactElement[]
  tidalStories: TidalStories
  tidalStoriesApi: TidalStoriesApi
}

export function TidalStoriesProvider(props: Props) {
  const { children, tidalStories, tidalStoriesApi } = props

  return (
    <tidalStoriesApiContext.Provider value={tidalStoriesApi}>
      <tidalStoriesContext.Provider value={tidalStories}>{children}</tidalStoriesContext.Provider>
    </tidalStoriesApiContext.Provider>
  )
}

// ------------------------------------------------------------------------
// Tidal Stories Context hook
// ------------------------------------------------------------------------

export function useTidalStories() {
  return useContext(tidalStoriesContext)
}

export function useTidalStoriesApi() {
  return useContext(tidalStoriesApiContext)
}

export function useUpdateGlobalConfigOnce(panel: keyof GlobalControlsConfig, value: GlobalControlConfig) {
  const { updateGlobalControlsConfig, setGlobalControlsConfig } = useTidalStoriesApi()

  // biome-ignore lint/correctness/useExhaustiveDependencies: should run once
  useEffect(() => {
    let valueToRestore: GlobalControlConfig = {}

    updateGlobalControlsConfig(prev => {
      valueToRestore = prev?.[panel] ?? {}
      return { [panel]: { ...prev?.[panel], ...value } }
    })

    return () => {
      setGlobalControlsConfig(prev => ({
        ...prev,
        [panel]: { ...valueToRestore },
      }))
    }
  }, [])
}

// #license-validation
validateTidalLicense()
